import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, IconButton, Tooltip } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import whatsappMicroserviceService from '../services/whatsappMicroserviceService';

const stripDigits = (value) => String(value || '').replace(/\D/g, '');

const normalizePhone = (phone) => {
  const digits = stripDigits(phone);
  if (!digits) return '';
  if (digits.startsWith('549') && digits.length === 13) return digits;
  if (digits.startsWith('54') && digits.length === 12) return `549${digits.slice(2)}`;
  if (digits.startsWith('9') && digits.length === 11) return `54${digits}`;
  if (digits.length === 10) return `549${digits}`;
  return digits;
};

const getStorageKey = (phone) => `whatsapp_unread_last_seen_${normalizePhone(phone)}`;
const getInitialLastSeen = () => new Date(Date.now() - 5 * 60 * 1000).toISOString();

const resolveAreaFromPath = (pathname = '') => {
  const path = String(pathname || '').toLowerCase();
  if (path.startsWith('/art')) return 'ART';
  if (path.startsWith('/rti')) return 'RTI';
  if (path.startsWith('/admin')) return 'ADMIN';
  if (path.startsWith('/recepcion')) return 'RECEPCION';
  if (path.startsWith('/judicial')) return 'JUDICIAL';
  return null;
};

function WhatsAppHeaderButton({ phone, clientName, onClick }) {
  const normalizedPhone = useMemo(() => normalizePhone(phone), [phone]);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadUnreadCount = useCallback(async () => {
    if (!normalizedPhone) {
      setUnreadCount(0);
      return;
    }

    try {
      const logs = await whatsappMicroserviceService.getLogs(200);
      if (!Array.isArray(logs)) {
        setUnreadCount(0);
        return;
      }

      const key = getStorageKey(normalizedPhone);
      const storedLastSeen = localStorage.getItem(key);
      const lastSeen = storedLastSeen || getInitialLastSeen();
      if (!storedLastSeen) {
        localStorage.setItem(key, lastSeen);
      }

      const incoming = logs.filter((log) => {
        const logPhone = normalizePhone(log.phone);
        return log.direction === 'incoming' && logPhone === normalizedPhone && log.timestamp && log.timestamp > lastSeen;
      });

      setUnreadCount(incoming.length);
    } catch {
      setUnreadCount(0);
    }
  }, [normalizedPhone]);

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 20000);
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  const handleClick = async () => {
    if (!normalizedPhone) return;

    const contexto = {
      phone: normalizedPhone,
      name: clientName || 'Cliente',
      area: resolveAreaFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
    };

    try {
      localStorage.setItem('whatsapp_current_context_v1', JSON.stringify(contexto));
      window.dispatchEvent(new CustomEvent('whatsappContextChanged', { detail: contexto }));
    } catch {
      // Ignorar fallos de almacenamiento local.
    }

    try {
      const logs = await whatsappMicroserviceService.getLogs(200);
      const incoming = Array.isArray(logs)
        ? logs.filter((log) => normalizePhone(log.phone) === normalizedPhone && log.direction === 'incoming' && log.timestamp)
        : [];
      const newestTimestamp = incoming.reduce(
        (acc, log) => (log.timestamp > acc ? log.timestamp : acc),
        localStorage.getItem(getStorageKey(normalizedPhone)) || getInitialLastSeen()
      );
      localStorage.setItem(getStorageKey(normalizedPhone), newestTimestamp || new Date().toISOString());
      setUnreadCount(0);
    } catch {
      setUnreadCount(0);
    }

    if (onClick) onClick();
  };

  const title = unreadCount > 0
    ? `${clientName || 'Cliente'} - ${unreadCount} mensaje${unreadCount === 1 ? '' : 's'} sin leer`
    : 'Abrir WhatsApp';

  return (
    <Tooltip title={title}>
      <span>
        <Badge
          badgeContent={unreadCount > 0 ? unreadCount : null}
          color="error"
          overlap="circular"
          max={99}
        >
          <IconButton
            onClick={handleClick}
            disabled={!normalizedPhone}
            aria-label={title}
            sx={{
              width: 36,
              height: 36,
              minWidth: 36,
              bgcolor: 'transparent',
              color: '#25D366',
              border: '2px solid #25D366',
              borderRadius: '20px',
              transition: 'all 0.25s ease-in-out',
              '&:hover': {
                bgcolor: 'rgba(37, 211, 102, 0.08)',
                boxShadow: 'var(--shadow-md)',
                borderColor: '#128C7E',
                color: '#128C7E'
              },
              '&:active': {
                boxShadow: 'var(--shadow-sm)'
              },
              '&.Mui-disabled': {
                bgcolor: 'transparent',
                color: '#ccc',
                borderColor: '#ccc'
              }
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Badge>
      </span>
    </Tooltip>
  );
}

WhatsAppHeaderButton.propTypes = {
  phone: PropTypes.string,
  clientName: PropTypes.string,
  onClick: PropTypes.func.isRequired
};

WhatsAppHeaderButton.defaultProps = {
  phone: '',
  clientName: 'Cliente'
};

export default WhatsAppHeaderButton;
